import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { UPDATE_COMPANY_LOGO, UPDATE_USER_PROFILE } from "../redux/action/ActionType";
import { DASHBOARD_ROUTE, SIGNIN_ROUTE } from "../constants/RoutePaths";
import { GET_TEAMDETAILS_BY_ID } from "../comman/url";
import API from "../constants/API";
import { authHeader } from "../comman/mainfunction";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

const SSOCallback = () => {
  const navigation = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Check for SSO error from backend redirect
        const ssoError = searchParams.get("sso_error");
        if (ssoError) {
          setError(decodeURIComponent(ssoError));
          setTimeout(() => navigation(SIGNIN_ROUTE), 3000);
          return;
        }

        const token = searchParams.get("token");
        const dataParam = searchParams.get("data");

        if (!token || !dataParam) {
          setError("Invalid SSO callback. Missing authentication data.");
          setTimeout(() => navigation(SIGNIN_ROUTE), 3000);
          return;
        }

        // Parse the login data (plain JSON from backend, URL-encoded)
        // TODO(types): SSO login response shape from backend
        const data: any = JSON.parse(decodeURIComponent(dataParam)); // TODO(types): API response shape

        if (!data || !data.userId) {
          setError("Authentication failed. Invalid response data.");
          setTimeout(() => navigation(SIGNIN_ROUTE), 3000);
          return;
        }

        // Store all localStorage keys (matches Signin.jsx exactly)
        // TODO(types): localStorage.setItem requires string; tolerated coercion preserved via String(...) wrapper
        const ls = localStorage;
        ls.setItem("User_ID", String(data.userId));
        ls.setItem("AUTH_TOKEN", token);
        ls.setItem("Team_ID", String(data.teamId));
        ls.setItem("EMAIL", data.email);
        ls.setItem("SENDER_EMAIL", data.invoiceSenderEmail);
        ls.setItem("USER_ROLE", data.userRole);
        ls.setItem("PROFILE_PIC", data.profileLogo);
        ls.setItem("APPROVAL_AMOUNT", String(data.approvalAmountTo));
        ls.setItem("INVOICE_COUNT", String(data.invoiceCount));
        ls.setItem("BRAND_LOGO", data.companyLogo);
        ls.setItem("AUTH_USER_DETAILS", JSON.stringify(data));
        ls.setItem("AUTH_2F", "0");
        ls.setItem("isException", String(data.exceptionRoleId || "0"));
        ls.setItem("isAddSupplier", String(data.isAddSupplier || 0));
        ls.setItem("LOGIN_NAME", data.firstName + " " + data.lastName);
        ls.setItem("API_TIME", new Date().toString());

        // Dispatch Redux actions
        dispatch({
          type: UPDATE_USER_PROFILE,
          payload: data.profileLogo,
          userName: data.firstName + " " + data.lastName,
        });
        dispatch({
          type: UPDATE_COMPANY_LOGO,
          payload: data.companyLogo,
        });

        // Fetch team details (same as Signin.jsx post-login)
        try {
          const config = {
            method: "GET",
            url: GET_TEAMDETAILS_BY_ID,
            headers: authHeader(),
            params: { teamId: data.teamId },
          };
          const response = await API(config);
          const teamResult = response.data;

          if (teamResult.status === "Success") {
            localStorage.setItem(
              "Suppliermaintanceemail",
              teamResult.data[0].supplierMaintenanceEmail
            );
          }
        } catch (teamErr: any) {
          // TODO(types): error object
          // Non-fatal: team details fetch failure shouldn't block login
          console.error("[SSOCallback] Team details fetch error:", teamErr);
        }

        // Navigate to dashboard
        navigation(DASHBOARD_ROUTE);
      } catch (err: any) {
        // TODO(types): error object
        console.error("[SSOCallback] Processing error:", err);
        setError("Sign-in failed. Redirecting to login page...");
        setTimeout(() => navigation(SIGNIN_ROUTE), 3000);
      }
    };

    processCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        gap: 2,
      }}
    >
      {error ? (
        <Typography color="error" variant="h6">
          {error}
        </Typography>
      ) : (
        <>
          <CircularProgress size={48} />
          <Typography variant="h6" color="textSecondary">
            Completing sign in...
          </Typography>
        </>
      )}
    </Box>
  );
};

export default SSOCallback;
